import { useNavigate, useSearchParams } from "react-router-dom";
import DefaultButton from "../../../../components/Buttons/Default";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { get } from "../../../../utils/helpers/api";
import AgGridTable from "../../../../components/Tables/AgGrid";
import DefaultPagination from "../../../../components/Pagination";
import ViewDrawer from "./Drawers/View";
import { printLeaveStatus, printLeaveType } from "../../utils/helpers";
import { useSettings } from "../../../../context/settings";

function LeaveRequestsPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const getLeaveRequests = async () => {
    setLoading(true);
    const data = await get(`/hr/leave-requests?page=${currentPage}&page_size=${pageSize}`);
    if (data?.success) {
      setRowData(data.leave_requests.data);
      setTotalPages(data.leave_requests.last_page);
      setTotalRecords(data.leave_requests.total);
    }
    setLoading(false);
  };

  useEffect(() => {
    getLeaveRequests();
  }, [currentPage, pageSize, searchParams]);

  const columnDefs = useMemo(() => [
    {
      field: 'employee.firstname',
      headerName: t('hr.employeeName'),
      flex: 1,
      sortable: true,
      valueFormatter: (params) => `${params.data.employee.firstname} ${params.data.employee.lastname}`
    },
    {
      field: 'leave_type',
      headerName: t('hr.leaveType'),
      width: 160,
      sortable: true,
      valueFormatter: (params) => printLeaveType(params.value)
    },
    { field: 'start_date', headerName: t('hr.startDate'), width: 130, sortable: true, valueFormatter: (p) => formatDate(p.value)},
    { field: 'end_date', headerName: t('hr.endDate'), width: 140, sortable: true, valueFormatter: (p) => formatDate(p.value)},
    { field: 'total_days', headerName: t('hr.totalDays'), width: 150, sortable: true},
    {
      field: 'status',
      headerName: t('common.status'),
      width: 120,
      sortable: true,
      cellRenderer: (params) => printLeaveStatus(params.value)
    },
    { field: 'order_date', headerName: t('hr.orderDate'), width: 120, sortable: true, valueFormatter: (p) => formatDate(p.value)}
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    setSelectedLeaveRequestId(event.data.id);
    setDrawerOpen(true);
  };

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('hr.leaveRequests')} onReload={getLeaveRequests} dataTotal={totalRecords}>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/hr/leave-requests/create')}>
            {t('hr.newLeaveRequest')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      <ViewDrawer
        leaveRequestId={selectedLeaveRequestId}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        refreshLeaveRequests={getLeaveRequests}
      />
    </SidebarLayout>
  );
}

export default LeaveRequestsPage;
